/*
 * <slider-nav data-slider="<id of the Flickity element>">
 *
 * Drives the arrows + progress bar rendered by snippets/slider-nav.liquid.
 *
 * Notes on the two ways sliders get built in this theme:
 *  - flickity-responsive (data-flickity-responsive) destroys and re-creates the
 *    instance on breakpoint changes, so the instance is re-resolved on resize.
 *  - product-recommendations injects its markup with innerHTML and builds
 *    Flickity afterwards, so the instance is polled for on connect. A custom
 *    element is used here precisely because it also upgrades in injected HTML,
 *    where an inline <script> would never run.
 */
if (!customElements.get('slider-nav')) {
  class SliderNav extends HTMLElement {
    connectedCallback() {
      this.thumb = this.querySelector('.slider-nav__bar-fill');
      this.leftButton = this.querySelector('[data-slider-nav-left]');
      this.rightButton = this.querySelector('[data-slider-nav-right]');
      if (!this.thumb || !this.leftButton || !this.rightButton) return;

      if (!this.wired) {
        this.wired = true;
        this.update = this.update.bind(this);
        this.bind = this.bind.bind(this);
        this.leftButton.addEventListener('click', () => this.go('left'));
        this.rightButton.addEventListener('click', () => this.go('right'));
        this.onResize = () => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(this.bind, 300);
        };
      }

      window.addEventListener('resize', this.onResize);

      // The slider may be built after this element connects (deferred Flickity,
      // lazily fetched recommendations), so keep looking for a while.
      var attempts = 0;
      this.poll = setInterval(() => {
        attempts += 1;
        if (this.bind() || attempts > 100) clearInterval(this.poll);
      }, 100);
    }

    disconnectedCallback() {
      clearInterval(this.poll);
      clearTimeout(this.resizeTimer);
      window.removeEventListener('resize', this.onResize);
    }

    get slider() {
      return document.getElementById(this.dataset.slider);
    }

    bind() {
      var slider = this.slider;
      var instance = slider && window.Flickity && window.Flickity.data(slider);
      if (!instance) return false;

      if (instance !== this.flkty) {
        this.flkty = instance;
        instance.on('select', this.update);
        instance.on('settle', this.update);
        instance.on('resize', this.update);
        // Cells are measured again once images have loaded.
        setTimeout(this.update, 600);
      }

      this.update();
      return true;
    }

    update() {
      var flkty = this.flkty;
      if (!flkty || !flkty.slides) return;

      var count = flkty.slides.length;
      // Nothing to scroll: no navigation to show.
      if (count < 2) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      var index = flkty.selectedIndex || 0;
      var progress = index / (count - 1);

      // Thumb length mirrors how much of the track is visible, like a scrollbar.
      var visibleRatio = 1 / count;
      if (flkty.slideableWidth && flkty.size && flkty.size.innerWidth) {
        visibleRatio = flkty.size.innerWidth / flkty.slideableWidth;
      }
      var thumbWidth = Math.min(100, Math.max(visibleRatio * 100, 8));

      this.thumb.style.width = thumbWidth + '%';
      this.thumb.style.left = progress * (100 - thumbWidth) + '%';

      // Flickity's own "previous" is the visually right-hand arrow in RTL sliders.
      var atStart = !flkty.options.wrapAround && index === 0;
      var atEnd = !flkty.options.wrapAround && index === count - 1;
      if (flkty.options.rightToLeft) {
        this.leftButton.disabled = atEnd;
        this.rightButton.disabled = atStart;
      } else {
        this.leftButton.disabled = atStart;
        this.rightButton.disabled = atEnd;
      }
    }

    go(direction) {
      if (!this.flkty) return;
      var forward = this.flkty.options.rightToLeft ? direction === 'left' : direction === 'right';
      if (forward) {
        this.flkty.next();
      } else {
        this.flkty.previous();
      }
    }
  }

  customElements.define('slider-nav', SliderNav);
}
